// src/pages/educator/ClassroomLeaderboard.js
//
// The across-the-room leaderboard.
//
// ===========================================================================
// WHAT THIS IS FOR
// ===========================================================================
// This is meant to be thrown on the smartboard and left there. Everything is
// sized to be read from the back of a classroom, it refreshes itself while
// class is running, and it has a real fullscreen mode so there's no browser
// chrome on the projector.
//
// It is NOT the per-topic leaderboard at /leaderboard/:slug — that one ranks
// every player who has ever touched one topic, across every school. This one
// ranks the children in ONE educator's class against each other, which is the
// version that actually motivates a room.
//
// ===========================================================================
// WHERE THE DATA COMES FROM
// ===========================================================================
// The same two sources EducatorClassroom.js already merges, for the same
// reasons documented at the top of that file:
//
//   classroomProgress/{educatorUid}/subjects/{subjectKey}/students/{uid}
//     The mirror. One collectionGroup query on educatorCode gets the whole
//     class in a single read.
//
//   educatorRosters/{code}/students
//     The roster, so a child who hasn't played yet still appears with 0
//     rather than vanishing. An empty row on the board is motivating; being
//     absent from the board entirely is not.
//
// It deliberately does NOT read leaderBoard_{slug}. Those documents are
// append-only — UniversalQuizGame addDoc()s a new one on every single play —
// so ranking a class from them would mean reading every attempt by every
// student in the world for every topic. The mirror already holds the rolled-up
// per-student numbers.
//
// ===========================================================================
// SCORING
// ===========================================================================
// A student's total is the sum of their BEST score on each topic, not the sum
// of every attempt. Two reasons:
//
//   - Summing attempts rewards replaying an easy topic twenty times over
//     working through new material. Kids find that within one class period.
//   - Best-per-topic means a retake can only ever help, so a child who did
//     badly the first time still has a reason to try again.
//
// ===========================================================================
// NAMES ON A PROJECTOR
// ===========================================================================
// Defaults to "Maya R." rather than the full name. A leaderboard is a screen
// pointed at a room that may contain visitors, other classes, or a window,
// and full student names on it is a FERPA conversation nobody wants to have.
// The educator can switch to full names with the toggle; the choice persists
// in localStorage per browser.

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  getFirestore,
  collectionGroup,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useNavigate, useParams } from "react-router-dom";
import { listRoster } from "../../utils/educatorCode";
import "./ClassroomLeaderboard.css";
import FocusAvatar from "../../components/avatar/FocusAvatar";

const SUBJECTS = [
  { key: "all", label: "All Subjects" },
  { key: "math", label: "Math" },
  { key: "science", label: "Science" },
  { key: "socialstudies", label: "Social Studies" },
  { key: "other", label: "Other" },
];

const RANGES = [
  { key: "all", label: "All Time", days: null },
  { key: "week", label: "This Week", days: 7 },
  { key: "today", label: "Today", days: 1 },
];

const REFRESH_MS = 45000;

const MEDALS = ["🥇", "🥈", "🥉"];

/** Turns whatever a timestamp was stored as into a Date, or null. */
const toDate = (raw) => {
  if (!raw) return null;
  if (raw.toDate) return raw.toDate();
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

const displayName = (student, full) => {
  const first = student.firstName || student.studentName?.split(" ")[0] || "Student";
  if (full) {
    return `${first} ${student.lastName || ""}`.trim();
  }
  const initial = (student.lastName || "")[0];
  return initial ? `${first} ${initial}.` : first;
};

/** Stable colour per student so the same child is the same colour every day. */
const hueFor = (seed = "") =>
  [...String(seed)].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;

// The student's built avatar (old picture avatars are retired). Tap it to see
// the whole body — one at a time.
function Avatar({ student, name, size = 62 }) {
  const uid = student.uid || student.studentUid || student.id;
  return <FocusAvatar id={`clb-${uid}`} uid={uid} size={size} fullSize={Math.max(170, size * 2.2)} name={name} />;
}

// ---------------------------------------------------------------------------

export default function ClassroomLeaderboard() {
  const db = getFirestore();
  const auth = getAuth();
  const navigate = useNavigate();
  const params = useParams();

  const [educator, setEducator] = useState(null);
  const [students, setStudents] = useState([]);
  const [progressByStudent, setProgressByStudent] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [lastRefresh, setLastRefresh] = useState(null);

  const [subject, setSubject] = useState(params.subject || "all");
  const [range, setRange] = useState("all");
  const [fullNames, setFullNames] = useState(
    () => localStorage.getItem("clbFullNames") === "1"
  );
  const [isFullscreen, setIsFullscreen] = useState(false);

  const shellRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("clbFullNames", fullNames ? "1" : "0");
  }, [fullNames]);

  // -------------------------------------------------------------------------
  // Load
  // -------------------------------------------------------------------------

  const load = useCallback(
    async (uid, { quiet = false } = {}) => {
      if (!quiet) setLoading(true);
      setLoadError("");

      try {
        const meSnap = await getDoc(doc(db, "users", uid));
        if (!meSnap.exists()) {
          setLoadError("We couldn't load your educator profile.");
          setLoading(false);
          return;
        }
        const me = { uid, ...meSnap.data() };
        setEducator(me);

        const code = me.educatorCode;
        if (!code) {
          setLoadError(
            "Your educator code hasn't been issued yet. Open your dashboard once and it will be created automatically."
          );
          setLoading(false);
          return;
        }

        // Roster first — see the note at the top of EducatorClassroom.js about
        // why this is educatorRosters and not a where() on /users.
        const rosterEntries = await listRoster(code);
        const roster = (
          await Promise.all(
            rosterEntries
              .filter((r) => r.uid !== uid)
              .filter((r) => !String(r.intendedRole || "").includes("educator"))
              .map(async (entry) => {
                try {
                  const snap = await getDoc(doc(db, "users", entry.uid));
                  return snap.exists()
                    ? { ...entry, ...snap.data(), uid: entry.uid }
                    : entry;
                } catch {
                  return entry;
                }
              })
          )
        ).filter(Boolean);

        setStudents(roster);

        // Progress mirror, whole class in one query.
        const merged = {};
        try {
          const mirrorSnap = await getDocs(
            query(collectionGroup(db, "students"), where("educatorCode", "==", code))
          );

          mirrorSnap.forEach((d) => {
            const subjectKey = d.ref.parent.parent?.id || "";
            const row = d.data();
            const sUid = row.studentUid || d.id;
            merged[sUid] = merged[sUid] || {};
            merged[sUid][subjectKey] = {
              ...(merged[sUid][subjectKey] || {}),
              ...(row.slugs || {}),
            };
          });
        } catch (err) {
          console.warn(
            "[ClassroomLeaderboard] mirror query unavailable, falling back to userData:",
            err?.code || err?.message
          );
        }

        // Gap-fill from userData for anyone the mirror hasn't caught up on.
        await Promise.all(
          roster.map(async (student) => {
            if (merged[student.uid]) return;
            try {
              const snap = await getDoc(doc(db, "userData", student.uid));
              if (!snap.exists()) return;
              const data = snap.data() || {};
              const buckets = {};
              Object.keys(data).forEach((k) => {
                if (data[k] && typeof data[k] === "object" && k.includes("_")) {
                  buckets[k] = data[k];
                }
              });
              merged[student.uid] = buckets;
            } catch {
              /* one unreadable student shouldn't blank the board */
            }
          })
        );

        setProgressByStudent(merged);
        setLastRefresh(new Date());
      } catch (err) {
        console.error("[ClassroomLeaderboard] load failed:", err);
        setLoadError("Something went wrong loading the leaderboard. Please refresh.");
      } finally {
        setLoading(false);
      }
    },
    [db]
  );

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u) load(u.uid);
      else setLoading(false);
    });
    return () => unsub();
  }, [auth, load]);

  // Quiet auto-refresh, so a board left up all period stays current without
  // ever flashing a loading state at the room.
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const t = setInterval(() => load(uid, { quiet: true }), REFRESH_MS);
    return () => clearInterval(t);
  }, [auth, load]);

  // -------------------------------------------------------------------------
  // Fullscreen
  // -------------------------------------------------------------------------

  const toggleFullscreen = () => {
    const el = shellRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.();
    }
  };

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  // -------------------------------------------------------------------------
  // Ranking
  // -------------------------------------------------------------------------

  const cutoff = useMemo(() => {
    const days = RANGES.find((r) => r.key === range)?.days;
    if (!days) return null;
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d;
  }, [range]);

  const rows = useMemo(() => {
    const scored = students.map((student) => {
      const buckets = progressByStudent[student.uid] || {};

      // Which subject buckets count toward this view.
      const keys = Object.keys(buckets).filter((k) =>
        subject === "all" ? true : k.endsWith(`_${subject}`)
      );

      let points = 0;
      let topics = 0;
      let plays = 0;
      let videos = 0;
      let songs = 0;
      let lastActive = null;

      keys.forEach((bucketKey) => {
        const bucket = buckets[bucketKey] || {};

        Object.keys(bucket).forEach((slug) => {
          const t = bucket[slug];
          if (!t || typeof t !== "object") return;

          const g = t.game || {};
          const when =
            toDate(g.timestamp) || toDate(g.lastPlayedAt) || toDate(t.lastVideoAt);

          // Range filter. A topic with no usable timestamp is counted in the
          // all-time view only — better than dropping real work off the board
          // because an old record predates the timestamp field.
          if (cutoff && (!when || when < cutoff)) return;

          if (when && (!lastActive || when > lastActive)) lastActive = when;

          videos += Number(t.videoQty || 0) || (t.video ? 1 : 0);
          songs += Number(t.musicQty || 0) || (t.music ? 1 : 0);

          // Best attempt on this topic, not the sum of attempts.
          const history = Array.isArray(g.score) ? g.score.filter((n) => typeof n === "number") : [];
          const best = Math.max(
            typeof g.latestTotal === "number" ? g.latestTotal : 0,
            history.length ? Math.max(...history) : 0
          );

          if (best > 0 || g.completed) {
            topics += 1;
            points += best;
            plays += history.length || Number(g.quizPlays || 0) || 1;
          }
        });
      });

      return {
        student,
        name: displayName(student, fullNames),
        points,
        topics,
        plays,
        videos,
        songs,
        lastActive,
      };
    });

    return scored.sort(
      (a, b) =>
        b.points - a.points ||
        b.topics - a.topics ||
        a.name.localeCompare(b.name)
    );
  }, [students, progressByStudent, subject, cutoff, fullNames]);

  // Competition ranking: equal scores share a place, and the next place skips.
  const ranked = useMemo(() => {
    let lastPoints = null;
    let lastRank = 0;
    return rows.map((row, i) => {
      const rank = row.points === lastPoints ? lastRank : i + 1;
      lastPoints = row.points;
      lastRank = rank;
      return { ...row, rank };
    });
  }, [rows]);

  const podium = ranked.filter((r) => r.points > 0).slice(0, 3);
  const classTotal = ranked.reduce((sum, r) => sum + r.points, 0);
  const topScore = ranked[0]?.points || 0;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="clb-shell">
        <div className="clb-message">Loading the leaderboard…</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="clb-shell">
        <div className="clb-message">
          <p>{loadError}</p>
          <button className="clb-btn" onClick={() => navigate("/educator/dashboard")}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`clb-shell ${isFullscreen ? "is-fullscreen" : ""}`} ref={shellRef}>
      {/* ------------------------------ Controls ---------------------------- */}
      <div className="clb-controls no-print">
        <button className="clb-btn ghost" onClick={() => navigate(-1)}>
          ← Back
        </button>

        <div className="clb-seg" role="tablist" aria-label="Subject">
          {SUBJECTS.map((s) => (
            <button
              key={s.key}
              role="tab"
              aria-selected={subject === s.key}
              className={`clb-seg-btn ${subject === s.key ? "on" : ""}`}
              onClick={() => setSubject(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="clb-seg" role="tablist" aria-label="Time range">
          {RANGES.map((r) => (
            <button
              key={r.key}
              role="tab"
              aria-selected={range === r.key}
              className={`clb-seg-btn ${range === r.key ? "on" : ""}`}
              onClick={() => setRange(r.key)}
            >
              {r.label}
            </button>
          ))}
        </div>

        <label className="clb-toggle">
          <input
            type="checkbox"
            checked={fullNames}
            onChange={(e) => setFullNames(e.target.checked)}
          />
          <span>Full names</span>
        </label>

        <button className="clb-btn ghost" onClick={toggleFullscreen}>
          {isFullscreen ? "⤢ Exit Fullscreen" : "⛶ Fullscreen"}
        </button>

        <button
          className="clb-btn"
          onClick={() => auth.currentUser && load(auth.currentUser.uid)}
        >
          ↻ Refresh
        </button>
      </div>

      {/* ------------------------------ Header ------------------------------ */}
      <header className="clb-header">
        <h1 className="clb-title">
          <span className="clb-trophy" aria-hidden="true">🏆</span>
          Class Leaderboard
        </h1>
        <p className="clb-sub">
          {educator?.firstName ? `${educator.firstName}'s class` : "Your class"}
          {" · "}
          {SUBJECTS.find((s) => s.key === subject)?.label}
          {" · "}
          {RANGES.find((r) => r.key === range)?.label}
        </p>
      </header>

      {/* ------------------------------ Podium ------------------------------ */}
      {podium.length > 0 && (
        <div className="clb-podium">
          {/* Rendered 2nd, 1st, 3rd so the winner is physically centred. */}
          {[podium[1], podium[0], podium[2]].map((row, i) =>
            row ? (
              <div
                key={row.student.uid}
                className={`clb-plinth place-${row.rank}`}
                style={{ "--delay": `${i * 120}ms` }}
              >
                <div className="clb-medal">{MEDALS[row.rank - 1]}</div>
                <Avatar student={row.student} name={row.name} size={row.rank === 1 ? 96 : 74} />
                <div className="clb-plinth-name">{row.name}</div>
                <div className="clb-plinth-points">{row.points.toLocaleString()}</div>
                <div className="clb-plinth-label">points</div>
                <div className="clb-plinth-base">{row.rank}</div>
              </div>
            ) : (
              <div key={`empty-${i}`} className="clb-plinth empty" />
            )
          )}
        </div>
      )}

      {/* ------------------------------ Table ------------------------------- */}
      {ranked.length === 0 ? (
        <div className="clb-message">
          <p>No students on your roster yet.</p>
          <p className="clb-hint">
            Share your educator code
            {educator?.educatorCode ? ` (${educator.educatorCode})` : ""} so students
            can join your class.
          </p>
        </div>
      ) : (
        <ol className="clb-list">
          {ranked.map((row) => {
            const barPct = topScore > 0 ? (row.points / topScore) * 100 : 0;
            return (
              <li
                key={row.student.uid}
                className={`clb-row ${row.rank <= 3 && row.points > 0 ? "top" : ""} ${
                  row.points === 0 ? "zero" : ""
                }`}
              >
                <span className="clb-rank">
                  {row.points > 0 && row.rank <= 3 ? MEDALS[row.rank - 1] : row.rank}
                </span>

                <Avatar student={row.student} name={row.name} size={54} />

                <span className="clb-name">{row.name}</span>

                <span className="clb-meta">
                  <span title="Topics completed">📘 {row.topics}</span>
                  <span title="Videos watched">🎬 {row.videos}</span>
                  <span title="Songs played">🎵 {row.songs}</span>
                </span>

                <span className="clb-bar" aria-hidden="true">
                  <span className="clb-bar-fill" style={{ width: `${barPct}%` }} />
                </span>

                <span className="clb-points">{row.points.toLocaleString()}</span>
              </li>
            );
          })}
        </ol>
      )}

      {/* ------------------------------ Footer ------------------------------ */}
      <footer className="clb-footer">
        <span>
          Class total: <strong>{classTotal.toLocaleString()}</strong> points
        </span>
        {lastRefresh && (
          <span className="clb-stamp">
            Updated{" "}
            {lastRefresh.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
            {" · refreshes automatically"}
          </span>
        )}
      </footer>
    </div>
  );
}
